import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Card,
  DataTable,
  Filter,
  RigoFilters,
  type ColumnDef,
} from "@/deps/ui-library";
import { useTrialBalanceAsOn } from "@/api/queries";
import type { AccountGroup, TrialBalanceAsOnRow } from "@/api/types";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { ReportFrame } from "./ReportFrame";
import { useDraftFilters } from "./useDraftFilters";
import { DateField } from "@/components/DateField";

const TYPES: Array<{ value: string; label: string }> = [
  { value: "Assets", label: "Assets" },
  { value: "Liabilities", label: "Liabilities" },
  { value: "Equity", label: "Equity" },
  { value: "Income", label: "Income" },
  { value: "Expenses", label: "Expenses" },
];

const figure = (value: string) => (Number(value) === 0 ? "" : value);

export function TrialBalancePage() {
  const search = useSearch({ strict: false }) as { asOn?: string; type?: string };
  const navigate = useNavigate();
  const asOn = search.asOn ?? today();
  const type = search.type ?? "all";
  const { data, isPending, error } = useTrialBalanceAsOn(asOn);

  // A draft until Apply, so changing the date doesn't reload the report under somebody reading it.
  const { draft, changed, edit } = useDraftFilters({ asOn, type });
  const apply = () => {
    if (changed) {
      void navigate({ to: "/reports/trial-balance", search: draft, replace: true });
    }
  };

  const rows = (data?.rows ?? []).filter(
    (row) => type === "all" || row.group === (type as AccountGroup),
  );
  const period = `As on ${formatDate(asOn)}`;

  const ledgerLink = (row: TrialBalanceAsOnRow, label: string) => (
    <Link
      to="/reports/general-ledger"
      search={{ account: row.code, from: data?.fiscalYearStart, to: asOn }}
      title="Open ledger"
      className="hover:underline"
    >
      {label}
    </Link>
  );

  const columns: Array<ColumnDef<TrialBalanceAsOnRow>> = [
    {
      id: "code",
      header: "Code",
      cell: ({ row: { original: row } }) => <span className="text-fg text-sm">{row.code}</span>,
    },
    {
      id: "account",
      header: "Account",
      cell: ({ row: { original: row } }) => ledgerLink(row, row.name),
    },
    {
      id: "class",
      header: "Class",
      cell: ({ row: { original: row } }) => <span className="text-fg text-sm">{row.group}</span>,
    },
    {
      id: "debit",
      header: "Debit",
      meta: { numeric: true },
      cell: ({ row: { original: row } }) =>
        figure(row.debit) ? ledgerLink(row, formatAmount(row.debit)) : "—",
    },
    {
      id: "credit",
      header: "Credit",
      meta: { numeric: true },
      cell: ({ row: { original: row } }) =>
        figure(row.credit) ? ledgerLink(row, formatAmount(row.credit)) : "—",
    },
  ];

  const shownDebit = rows.reduce((sum, row) => sum + Number(row.debit), 0);
  const shownCredit = rows.reduce((sum, row) => sum + Number(row.credit), 0);

  return (
    <ReportFrame
      id="tb"
      title="Trial balance"
      period={period}
      exportable={
        data
          ? () => ({
              title: "Trial balance",
              period,
              columns: [
                { header: "Code" },
                { header: "Account" },
                { header: "Class" },
                { header: "Debit", numeric: true },
                { header: "Credit", numeric: true },
              ],
              rows: rows.map((row) => [row.code, row.name, row.group, row.debit, row.credit]),
              totals: ["Total", null, null, shownDebit.toFixed(2), shownCredit.toFixed(2)],
            })
          : undefined
      }
    >
      <RigoFilters.Root>
        <RigoFilters.StaticRow>
          <DateField
            aria-label="As on"
            variant="filter"
            clearable={false}
            value={draft.asOn}
            onChange={(value) => {
              if (value) edit({ asOn: value });
            }}
          />
          <Filter.SingleSelect
            items={TYPES}
            placeholder="All account types"
            searchable={false}
            value={TYPES.find((option) => option.value === draft.type) ?? null}
            onValueChange={(item) => edit({ type: item ? String(item.value) : "all" })}
          />
          <Button
            colorScheme="primary"
            variant="secondary"
            size="sm"
            disabled={!changed}
            onClick={apply}
          >
            Apply
          </Button>
        </RigoFilters.StaticRow>
      </RigoFilters.Root>

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={error instanceof Error ? error.message : "That report could not be loaded."}
        />
      ) : null}

      <Card size="none" className="overflow-hidden">
        <DataTable
          aria-label="Trial balance"
          size="sm"
          enableSorting={false}
          columns={columns}
          data={rows}
          getRowId={(row) => row.accountId}
          loading={isPending}
          emptyContent="No account has movement up to this date."
          pagination={{ mode: "manual", show: false }}
          containerClassName="rounded-none border-0"
        />
        <div className="border-line text-fg flex items-center justify-between border-t px-3 py-2 text-sm font-semibold">
          <span>Total</span>
          <span className="flex gap-8">
            <span>{formatAmount(shownDebit.toFixed(2))}</span>
            <span>{formatAmount(shownCredit.toFixed(2))}</span>
          </span>
        </div>
      </Card>

      {data && type === "all" ? (
        data.totalDebit === data.totalCredit ? (
          <p className="text-ok-text text-xs">Debits equal credits.</p>
        ) : (
          <p role="alert" className="text-bad-text text-xs">
            Debits and credits differ by{" "}
            {formatAmount((Number(data.totalDebit) - Number(data.totalCredit)).toFixed(2))}.
          </p>
        )
      ) : null}
    </ReportFrame>
  );
}
